import { Building2, MapPin, ShieldCheck } from "lucide-react";

import { useQuery } from "@tanstack/react-query";
import type { CoachAffiliationStatus } from "@flexv-coach/shared";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Card, CardContent, CardHeader } from "@/components/ui/Card";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/ui/States";
import { coachApi } from "@/lib/api";
import { employmentLabel } from "@/lib/labels";
import { useI18n } from "@/hooks/useI18n";

const STATUS_TONE: Record<CoachAffiliationStatus, "success" | "warning" | "danger" | "neutral"> = {
    active: "success",
    pending_approval: "warning",
    rejected: "danger",
    inactive: "neutral",
    terminated: "danger",
};

/**
 * Menu Affiliation — daftar afiliasi coach ke outlet/company.
 *
 * Saat ini READ-ONLY: afiliasi dibuat & disetujui oleh merchant di portal
 * utama. Alur pengajuan dua arah (coach → outlet, outlet → coach) menyusul;
 * struktur halaman sudah disiapkan untuk itu.
 */
export function AffiliationsPage() {
    const { t } = useI18n();

    const { data, isPending, isError, error, refetch } = useQuery({
        queryKey: ["coach-affiliations"],
        queryFn: ({ signal }) => coachApi.affiliations(signal),
    });

    return (
        <div className="flex flex-col gap-6">
            <header>
                <h1 className="app-text-primary text-2xl font-bold sm:text-3xl">
                    {t("affiliations.title")}
                </h1>
                <p className="app-shell-text-muted mt-1 text-sm">
                    {t("affiliations.subtitle")}
                </p>
            </header>

            <Alert tone="info">{t("affiliations.readOnlyNote")}</Alert>

            {isPending && <LoadingBlock label={t("affiliations.loading")} />}

            {isError && (
                <Card>
                    <ErrorState
                        message={error instanceof Error ? error.message : t("affiliations.loadFailed")}
                        onRetry={() => void refetch()}
                    />
                </Card>
            )}

            {data && data.length === 0 && (
                <Card>
                    <EmptyState
                        icon={<Building2 className="h-6 w-6" />}
                        title={t("affiliations.empty.title")}
                        description={t("affiliations.empty.description")}
                    />
                </Card>
            )}

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {data?.map((affiliation) => (
                    <Card key={affiliation.id} hover>
                        <CardHeader
                            title={affiliation.outlet?.name ?? t("affiliations.companyWide")}
                            description={
                                affiliation.outlet?.city ?? affiliation.outlet?.address ?? undefined
                            }
                            icon={<Building2 className="h-5 w-5" />}
                            action={
                                <div className="flex items-center gap-2">
                                    {affiliation.isPrimary && (
                                        <Badge tone="primary">{t("affiliations.primary")}</Badge>
                                    )}
                                    <Badge tone={STATUS_TONE[affiliation.status]}>
                                        {t(`affiliations.status.${affiliation.status}`)}
                                    </Badge>
                                </div>
                            }
                        />
                        <CardContent className="flex flex-col gap-3">
                            <dl className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <dt className="app-text-soft text-xs uppercase tracking-wide">
                                        {t("affiliations.type")}
                                    </dt>
                                    <dd className="app-text-primary font-semibold">
                                        {employmentLabel(affiliation.employmentType)}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="app-text-soft text-xs uppercase tracking-wide">
                                        {t("affiliations.scope")}
                                    </dt>
                                    <dd className="app-text-primary font-semibold">
                                        {affiliation.outletId
                                            ? t("affiliations.scopeOutlet")
                                            : t("affiliations.scopeCompany")}
                                    </dd>
                                </div>
                            </dl>

                            {affiliation.outlet?.address && (
                                <p className="app-shell-text-muted flex items-start gap-2 text-sm">
                                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                                    {affiliation.outlet.address}
                                </p>
                            )}

                            {affiliation.status === "active" && (
                                <p className="app-shell-text-muted flex items-center gap-2 text-xs">
                                    <ShieldCheck className="h-4 w-4 text-[var(--color-success-text)]" />
                                    {t("affiliations.verifiedNote")}
                                </p>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}
